import React from 'react';
import { type AccessibilityRole, Platform, View } from 'react-native';

import { Skeleton, createMqStyles, tokens } from '@fhs/ui';

import { useStoreDetailsMqStyles } from './store-details.styles';

const isWeb = Platform.OS === 'web';

/** A full week, which is what the panel always shows once the store arrives. */
const PLACEHOLDER_HOURS = [0, 1, 2, 3, 4, 5, 6];
const PLACEHOLDER_SERVICES = [0, 1, 2, 3];

/**
 * Placeholder shown while the store is fetched. The geometry comes from the panel's own
 * styles so the details land in place rather than replacing a spinner with a full screen
 * of content.
 */
export const StoreDetailsSkeleton: React.VFC = () => {
  const panelStyles = useStoreDetailsMqStyles();
  const styles = useStoreDetailsSkeletonMqStyles();

  return (
    <View
      testID="store-details-loading"
      style={styles.container}
      accessible
      accessibilityLabel="Loading store details. Please wait."
      accessibilityRole={(isWeb ? 'status' : 'text') as AccessibilityRole}
      accessibilityLiveRegion="polite"
      {...(isWeb
        ? ({ role: 'status', 'aria-live': 'polite', 'aria-busy': true } as Record<string, unknown>)
        : {})}
    >
      <View style={[panelStyles.scrollContent, styles.content]}>
        <View style={panelStyles.summary}>
          <Skeleton style={[styles.line, styles.lineStatus]} />
          <Skeleton style={[styles.line, styles.lineName]} />
          <Skeleton style={[styles.line, styles.lineAddress]} />
          <Skeleton style={[styles.line, styles.lineLocality]} />
        </View>

        <View style={panelStyles.actionRow}>
          <Skeleton style={styles.actionButton} />
          <Skeleton style={styles.actionButton} />
        </View>

        <View style={panelStyles.serviceRow}>
          {PLACEHOLDER_SERVICES.map(index => (
            <Skeleton key={index} style={styles.serviceChip} />
          ))}
        </View>

        <View style={panelStyles.hours}>
          <Skeleton style={[styles.line, styles.lineHoursHeading]} />

          {PLACEHOLDER_HOURS.map(index => (
            <View key={index} style={panelStyles.hoursRow}>
              <Skeleton style={[styles.line, styles.lineDay]} />
              <Skeleton style={[styles.line, styles.lineInterval]} />
            </View>
          ))}
        </View>
      </View>

      <View style={panelStyles.footer}>
        <Skeleton style={styles.cta} />
      </View>
    </View>
  );
};

const useStoreDetailsSkeletonMqStyles = createMqStyles({
  container: {
    $base: {
      flex: 1,
      overflow: 'hidden',
    },
  },
  // Takes the scroll view's place, so the footer stays at the bottom of the panel.
  content: {
    $base: {
      flex: 1,
    },
  },
  line: {
    $base: {
      backgroundColor: tokens.colors.$blackOpacity04,
      borderRadius: 16,
      height: 20,
      width: '100%',
    },
    $gteDesktop: {
      height: 24,
    },
  },
  lineStatus: {
    $base: {
      height: 16,
      maxWidth: 180,
    },
    $gteDesktop: {
      height: 18,
    },
  },
  // The store name is a heading, so it stands taller than the lines under it.
  lineName: {
    $base: {
      height: 28,
      maxWidth: 200,
    },
    $gteDesktop: {
      height: 32,
    },
  },
  lineAddress: {
    $base: {
      maxWidth: 240,
    },
  },
  lineLocality: {
    $base: {
      maxWidth: 190,
    },
  },
  // Call and Get Directions share the row evenly, as the real pair does.
  actionButton: {
    $base: {
      backgroundColor: tokens.colors.$blackOpacity04,
      borderRadius: 999,
      flexBasis: 0,
      flexGrow: 1,
      flexShrink: 1,
      height: 40,
    },
  },
  serviceChip: {
    $base: {
      backgroundColor: tokens.colors.$blackOpacity04,
      borderRadius: 4,
      flexBasis: 0,
      flexGrow: 1,
      flexShrink: 1,
      height: 32,
    },
  },
  lineHoursHeading: {
    $base: {
      height: 24,
      maxWidth: 140,
    },
    $gteDesktop: {
      height: 28,
    },
  },
  lineDay: {
    $base: {
      maxWidth: 90,
      width: 90,
    },
  },
  lineInterval: {
    $base: {
      maxWidth: 160,
      width: 160,
    },
  },
  cta: {
    $base: {
      backgroundColor: tokens.colors.$blackOpacity04,
      borderRadius: 999,
      height: 48,
      width: '100%',
    },
  },
});
